import {
  Alert,
  Button,
  Checkbox,
  Col,
  Empty,
  Input,
  Row,
  Spin,
  Table,
} from 'antd';
import {Link} from 'react-router-dom';
import moment from 'moment';
import {SearchOutlined, ReloadOutlined} from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import {get, isEqual} from 'lodash';

import {StudentsService} from './StudentsService';
import {useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {setLastSeenOnline} from '../../redux/actions/user';
import {useTablePagination} from '../../hooks/useTablePagination';

const studentsService = new StudentsService();

export const StudentsTable = () => {
  const dispatch = useDispatch();
  const [students, setStudents] = useState<any>();
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchBy, setSearchBy] = useState<number>(0);
  const timeoutRef = useRef<any>();
  const [emptySearchValue, setEmptySearchValue] = useState<boolean>(true);
  const [searchedColumn, setSearchedColumn] = useState<any>();
  const [searchText, setSearchText] = useState<any>();
  const searchInput = useRef<any>(null);
  const [searchValue, setSearchValue] = useState<string>('');

  const {
    current,
    setCurrent,
    pageSize,
    setTotal,
    paginationConfig,
    paginationSelector,
  } = useTablePagination({
    itemName: 'students',
    defaultPageSize: 100,
  });

  const fetchStudents = async (
    page: number = current,
    forcedSearchValue?: string,
    forcedSearchBy?: number,
  ) => {
    setIsLoading(true);
    setIsError(false);
    dispatch(setLastSeenOnline());

    try {
      const offset = (page - 1) * pageSize;
      const isSearch = !emptySearchValue || !!forcedSearchValue;
      const query = forcedSearchValue ?? searchValue;
      const effectiveSearchBy = forcedSearchBy ?? searchBy;
      const response = await studentsService.getStudents(
        String(offset),
        isSearch ? (effectiveSearchBy === 1 ? 'ID' : 'NAME') : undefined,
        isSearch ? query : undefined,
        pageSize,
      );

      setStudents(response);
      setTotal(response?.totalCount ?? response?.items?.length ?? 0);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, pageSize, emptySearchValue]);

  const getColumnSearchProps = (dataIndex: any) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: any) => (
      <div style={{padding: 8}}>
        <Input
          ref={(node: any) => {
            searchInput.current = node;
          }}
          placeholder={`Search ${Array.isArray(dataIndex) ? dataIndex.join(' ') : dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => {
            setSelectedKeys(e.target.value ? [e.target.value] : []);
          }}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{marginBottom: 8, display: 'block'}}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<SearchOutlined />}
          size="small"
          style={{width: 90, marginRight: 8}}>
          Search
        </Button>
        <Button
          onClick={() => handleReset(clearFilters)}
          size="small"
          style={{width: 90}}>
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered: any) => (
      <SearchOutlined style={{color: filtered ? '#1890ff' : undefined}} />
    ),
    onFilter: (value: any, record: any) =>
      get(record, dataIndex)
        ? get(record, dataIndex)
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : '',
    onFilterDropdownVisibleChange: (visible: any) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select?.(), 100);
      }
    },
    render: (text: any) =>
      isEqual(searchedColumn, dataIndex) ? (
        <Highlighter
          highlightStyle={{backgroundColor: '#ffc069', padding: 0}}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const handleSearch = (selectedKeys: any, confirm: any, dataIndex: any) => {
    confirm();
    setSearchedColumn(dataIndex);
    setSearchText(selectedKeys[0]);
  };

  const handleReset = (clearFilters: any) => {
    clearFilters();
    setSearchText('');
  };

  const columns: any[] = [
    {
      title: 'Name',
      dataIndex: 'name',
      ...getColumnSearchProps('name'),
      sorter: (a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? ''),
      render: (_: string, record: any) => (
        <Link
          to={{
            pathname: '/students/' + record.id + '/edit',
            state: {student: record},
          }}>
          {record.name || 'Unknown'}
        </Link>
      ),
    },
    {
      title: 'Student ID',
      dataIndex: 'id',
      ...getColumnSearchProps('id'),
      sorter: (a: any, b: any) => (a.id ?? '').localeCompare(b.id ?? ''),
      render: (id: string, record: any) => (
        <Link
          to={{
            pathname: '/students/' + record.id + '/edit',
            state: {student: record},
          }}>
          {id}
        </Link>
      ),
    },
    {
      title: 'Gender',
      dataIndex: 'gender',
      sorter: (a: any, b: any) =>
        (a.gender ?? '').localeCompare(b.gender ?? ''),
    },
    {
      title: 'Age',
      dataIndex: 'age',
      sorter: (a: any, b: any) => (a.age ?? 0) - (b.age ?? 0),
    },
    {
      title: 'User Email',
      dataIndex: ['user', 'email'],
      ...getColumnSearchProps(['user', 'email']),
    },
    {
      title: 'Whatsapp',
      dataIndex: ['user', 'whatsappNumber'],
      ...getColumnSearchProps(['user', 'whatsappNumber']),
    },
    {
      title: 'School ID',
      dataIndex: 'schoolId',
      ...getColumnSearchProps('schoolId'),
    },
    {
      title: 'Class ID',
      dataIndex: 'classSchoolId',
      ...getColumnSearchProps('classSchoolId'),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      sorter: (a: any, b: any) =>
        moment(a.createdAt).unix() - moment(b.createdAt).unix(),
      render: (value: string) =>
        value ? moment(value).format('YYYY-MM-DD HH:mm') : '-',
    },
  ];

  return (
    <>
      <Row
        style={{
          margin: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}>
        <Col>
          <div style={{display: 'flex', gap: 12}}>
            <Checkbox
              checked={searchBy === 0}
              onChange={() => {
                setSearchBy(0);
                setCurrent(1);
                if (searchValue) {
                  fetchStudents(1, searchValue, 0);
                }
              }}>
              Search By Name
            </Checkbox>
            <Checkbox
              checked={searchBy === 1}
              onChange={() => {
                setSearchBy(1);
                setCurrent(1);
                if (searchValue) {
                  fetchStudents(1, searchValue, 1);
                }
              }}>
              Search By ID
            </Checkbox>
          </div>
          <Input
            style={{marginTop: 10, width: 320}}
            placeholder={`Search students by ${searchBy === 1 ? 'ID' : 'name'}`}
            onChange={event => {
              setIsError(false);
              const value = event.target.value;
              setSearchValue(value);

              if (value.length === 0) {
                setEmptySearchValue(true);
                setCurrent(1);
              } else {
                setEmptySearchValue(false);
              }

              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }
              timeoutRef.current = setTimeout(() => {
                setCurrent(1);
                fetchStudents(1, value);
              }, 700);
            }}
          />
        </Col>
        <Col style={{display: 'flex', gap: 10}}>
          {paginationSelector}
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setCurrent(1);
              fetchStudents(1);
            }}>
            Refresh
          </Button>
        </Col>
      </Row>

      {isError && (
        <Alert
          style={{margin: '0 20px 20px 20px'}}
          type="error"
          showIcon
          message="Students fetching failed"
          description="Please retry. If the issue persists, check network/auth configuration."
        />
      )}

      {!isError && !isLoading && (students?.items?.length ?? 0) === 0 && (
        <Empty description="No students found" style={{marginTop: 60}} />
      )}

      {!isError && !isLoading && (students?.items?.length ?? 0) > 0 && (
        <Table
          rowKey="id"
          pagination={paginationConfig}
          columns={columns}
          dataSource={students?.items}
        />
      )}

      {isLoading && (
        <Spin size="large" style={{margin: 'auto', width: '100%'}} />
      )}
    </>
  );
};
